import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Users,
    Download,
    ArrowSquareOut,
    Lightning,
    Star,
} from "@phosphor-icons/react";

import ContactStatusSelect from "@/components/ContactStatusSelect";
import { downloadCsv, csvFilenameFromRun } from "@/lib/csvDownload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTER_TABS = [
    { id: "queue", label: "Outreach Queue", testId: "queue-tab" },
    { id: "new", label: "New", testId: "new-tab" },
    { id: "high", label: "High Priority", testId: "high-tab" },
    { id: "contacted", label: "Contacted", testId: "contacted-tab" },
    { id: "all", label: "All", testId: "all-tab" },
];

export default function OutreachQueue({ category, location, runId }) {
    const [filter, setFilter] = useState("queue");
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!category || !location) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                category,
                location,
                filter,
                limit: "200",
            });
            const { data } = await axios.get(`${API}/providers?${params}`);
            setProviders(data.providers || []);
        } catch (e) {
            console.error("providers load fail", e);
        } finally {
            setLoading(false);
        }
    }, [category, location, filter]);

    useEffect(() => {
        load();
    }, [load]);

    const updateStatus = useCallback(
        async (providerId, nextStatus, prevStatus) => {
            setProviders((prev) =>
                prev.map((p) =>
                    p.id === providerId ? { ...p, contact_status: nextStatus } : p,
                ),
            );
            try {
                await axios.patch(`${API}/providers/${providerId}`, {
                    contact_status: nextStatus,
                });
                toast.success(`Marked as ${nextStatus}`);
                if (filter === "queue" || filter === "contacted") load();
            } catch (e) {
                setProviders((prev) =>
                    prev.map((p) =>
                        p.id === providerId ? { ...p, contact_status: prevStatus } : p,
                    ),
                );
                toast.error("Failed to update status");
            }
        },
        [filter, load],
    );

    const downloadQueueCsv = useCallback(
        async (mode) => {
            if (!runId) {
                toast.error("Run a search first to download this queue as CSV.");
                return;
            }
            const url = `${API}/runs/${runId}/export.csv?mode=${mode}`;
            try {
                const filename = csvFilenameFromRun({ category, location }, mode);
                const { rows } = await downloadCsv(url, filename);
                if (rows === 0) {
                    toast.warning(`CSV downloaded but the "${mode}" view has 0 rows right now.`);
                } else {
                    toast.success(`Downloaded ${rows} row${rows === 1 ? "" : "s"} → ${filename}`);
                }
            } catch (e) {
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail || e.message;
                if (status === 401) {
                    toast.error("Session expired — please sign in again.");
                } else {
                    toast.error(`CSV download failed: ${detail || "unknown error"}`);
                }
            }
        },
        [runId, category, location],
    );

    const counts = useMemo(() => {
        const c = { queue: 0, new: 0, high: 0, contacted: 0, all: providers.length };
        for (const p of providers) {
            if (p.priority_label === "High") c.high++;
            if (p.last_diff_status === "new") c.new++;
        }
        return c;
    }, [providers]);

    if (!category || !location) {
        return null;
    }

    return (
        <div className="panel" data-testid="outreach-queue">
            <div className="panel-header">
                <div className="flex items-center gap-2">
                    <Users size={16} weight="bold" />
                    <span className="label-kicker">Outreach Queue</span>
                    <span className="mono text-[11px] text-[#52525b]">
                        {category} · {location}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn-secondary"
                        style={{ padding: "6px 10px", fontSize: 11 }}
                        onClick={() => downloadQueueCsv("queue")}
                        disabled={!runId}
                        data-testid="download-queue-csv"
                    >
                        <Download size={12} weight="bold" />
                        Queue CSV
                    </button>
                    <button
                        className="btn-secondary"
                        style={{ padding: "6px 10px", fontSize: 11 }}
                        onClick={() => downloadQueueCsv("new")}
                        disabled={!runId}
                        data-testid="download-new-csv"
                    >
                        <Download size={12} weight="bold" />
                        New Only CSV
                    </button>
                    <button
                        className="btn-secondary"
                        style={{ padding: "6px 10px", fontSize: 11 }}
                        onClick={() => downloadQueueCsv("high")}
                        disabled={!runId}
                        data-testid="download-high-csv"
                    >
                        <Download size={12} weight="bold" />
                        High Priority CSV
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-[#e4e4e7] px-5 pt-3 flex gap-0 overflow-x-auto">
                {FILTER_TABS.map((t) => {
                    const active = filter === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setFilter(t.id)}
                            className={
                                "px-4 py-2 mono text-[11px] border-b-2 transition-colors whitespace-nowrap " +
                                (active
                                    ? "border-black font-bold"
                                    : "border-transparent text-[#52525b] hover:text-black")
                            }
                            data-testid={t.testId}
                        >
                            {t.label.toUpperCase()}
                        </button>
                    );
                })}
            </div>

            {/* Body */}
            {loading && (
                <div className="p-10 text-center text-[#52525b] text-sm">
                    Loading queue…
                </div>
            )}
            {!loading && providers.length === 0 && (
                <div className="p-10 text-center text-[#52525b] text-sm" data-testid="queue-empty">
                    {filter === "queue"
                        ? "No providers in the actionable queue. Run a fresh search or widen the priority threshold."
                        : `No ${filter} providers yet.`}
                </div>
            )}
            {!loading && providers.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="brutal-table" data-testid="queue-table">
                        <thead>
                            <tr>
                                <th style={{ width: 36 }}>#</th>
                                <th>Provider</th>
                                <th>Phone</th>
                                <th>Email</th>
                                <th>Website</th>
                                <th>Priority</th>
                                <th>Last</th>
                                <th style={{ width: 180 }}>Contact Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {providers.map((p, i) => (
                                <tr key={p.id} data-testid={`queue-row-${i}`}>
                                    <td className="mono text-[10px] text-[#a1a1aa]">
                                        {String(i + 1).padStart(2, "0")}
                                    </td>
                                    <td>
                                        <div className="font-medium flex items-center gap-2">
                                            {p.name || "—"}
                                            {p.last_diff_status === "new" && (
                                                <span
                                                    className="mono text-[9px] px-1.5 py-0.5 bg-black text-white"
                                                    data-testid="new-badge"
                                                >
                                                    NEW
                                                </span>
                                            )}
                                            {p.last_diff_status === "updated" && (
                                                <span
                                                    className="mono text-[9px] px-1.5 py-0.5 border border-[#b07000] text-[#b07000]"
                                                    data-testid="updated-badge"
                                                >
                                                    UPDATED
                                                </span>
                                            )}
                                        </div>
                                        <div className="label-kicker mt-0.5" style={{ fontSize: 9 }}>
                                            seen {p.run_count}× · sources:{" "}
                                            {(p.sources || []).join(", ") || "—"}
                                        </div>
                                    </td>
                                    <td className="mono text-xs whitespace-nowrap">
                                        {p.phone || <span className="text-[#a1a1aa]">—</span>}
                                    </td>
                                    <td className="mono text-xs break-all">
                                        {p.email || <span className="text-[#a1a1aa]">—</span>}
                                    </td>
                                    <td className="mono text-xs">
                                        {p.website ? (
                                            <a
                                                href={p.website}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 underline underline-offset-2"
                                            >
                                                {new URL(p.website).host.replace(/^www\./, "")}
                                                <ArrowSquareOut size={11} />
                                            </a>
                                        ) : (
                                            <span className="text-[#a1a1aa]">—</span>
                                        )}
                                    </td>
                                    <td>
                                        <PriorityBadge label={p.priority_label} score={p.priority_score} />
                                    </td>
                                    <td className="mono text-[10px] text-[#52525b] whitespace-nowrap">
                                        {fmtRelative(p.last_seen_at)}
                                    </td>
                                    <td>
                                        <ContactStatusSelect
                                            value={p.contact_status}
                                            onChange={(s) =>
                                                updateStatus(p.id, s, p.contact_status)
                                            }
                                            testId={`status-${i}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="px-5 py-3 border-t border-[#e4e4e7] flex items-center justify-between text-[10px] text-[#52525b]">
                <span>
                    Status changes persist across runs — this is your operational memory.
                </span>
                <span className="mono">
                    {counts.all} total · {counts.new} new · {counts.high} high-priority
                </span>
            </div>
        </div>
    );
}

function PriorityBadge({ label, score }) {
    const color =
        label === "High" ? "#002FA7" : label === "Medium" ? "#b07000" : "#a1a1aa";
    const Icon = label === "High" ? Lightning : label === "Medium" ? Star : null;
    return (
        <span
            className="inline-flex items-center gap-1 mono text-[10px] px-2 py-0.5"
            style={{ color, border: `1px solid ${color}` }}
        >
            {Icon && <Icon size={11} weight="fill" />}
            {label || "—"} {score ? `· ${score}` : ""}
        </span>
    );
}

function fmtRelative(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diffMs = Date.now() - d.getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return d.toLocaleDateString();
    } catch {
        return "—";
    }
}
